import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { useMatch } from 'react-router-dom';

// project imports
import { AuthHeader, ApiUrl } from 'utils/Constants';

const CurrencyContext = createContext(null);

/**
 * لیست ارزها
 * اگر بعداً ارز جدید اضافه شد، فقط همینجا اضافه کن.
 */
const CURRENCIES = [
    {
        value: 'Toman',
        label: 'تومان',
        rate: 1
    },
    {
        value: 'Rial',
        label: 'ریال',
        rate: 10
    },
    {
        value: 'Dollar',
        label: 'دلار',
        rate: 1 / 150000
    }
];

/**
 * مقدار global برای استفاده خارج از hook
 */
let globalCurrency = 'Toman';
let globalCurrencyLabel = 'تومان';

/**
 * تابع تبدیل اعداد واحدهای پولی
 */
export function convertCurrencyBase(amount, from = globalCurrency, to = 'Toman') {
    if (from === to) return amount;

    const fromCurrency = CURRENCIES.find((item) => item.value.toLowerCase() === String(from).toLowerCase());
    const toCurrency = CURRENCIES.find((item) => item.value.toLowerCase() === String(to).toLowerCase());
    return (amount * toCurrency.rate) / fromCurrency.rate;
}

/**
 * تابع تبدیل به واحد پایه
 */
export function currencyToBase(amount, from = globalCurrency, to = 'Toman') {
    return convertCurrencyBase(amount, from, to);
}

/**
 * تابع تبدیل از واحد پایه
 */
export function currencyFromBase(amount, from = 'Toman', to = globalCurrency) {
    return convertCurrencyBase(amount, from, to);
}

/**
 * تبدیل value ارز به label فارسی
 */
export function currencyToLabel(currency) {
    if (!currency) return 'تومان';

    const foundCurrency = CURRENCIES.find((item) => item.value.toLowerCase() === String(currency).toLowerCase());

    return foundCurrency?.label || currency;
}

/**
 * escape برای ساخت regex امن
 */
function escapeRegex(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * تابع string خروجی برای استفاده راحت در label, placeholder, title و...
 *
 * مثال:
 * currencyText('مبلغ ({label})')
 * currencyText('هزینه به تومان')
 * currencyText('از تومان به ریال')
 */
export function currencyText(text = '{label}') {
    if (!text) return '';

    let result = String(text);

    /**
     * جایگزینی placeholder
     */
    if (result.includes('{label}')) {
        result = result.replaceAll('{label}', globalCurrencyLabel);
    }

    /**
     * جایگزینی همه labelهای شناخته‌شده مثل تومان، ریال، دلار
     */
    const currencyLabels = CURRENCIES.map((item) => item.label);

    const regex = new RegExp(currencyLabels.map(escapeRegex).join('|'), 'g');

    result = result.replace(regex, globalCurrencyLabel);

    return result;
}

/**
 * اگر جایی خواستی مقدار فعلی currency را بدون hook بگیری
 */
export function getCurrentCurrency() {
    return globalCurrency;
}

/**
 * اگر جایی خواستی label فعلی currency را بدون hook بگیری
 */
export function getCurrentCurrencyLabel() {
    return globalCurrencyLabel;
}

export function CurrencyProvider({ children, initialCurrency = 'Toman' }) {
    /**
     * چون گفتی useMatch جواب داده، برای basisId از useMatch استفاده می‌کنیم.
     *
     * این دو حالت را پوشش می‌دهد:
     * /basis/96
     * /basis/96/anything
     */
    const basisMatch = useMatch('/basis/:basisId');
    const basisNestedMatch = useMatch('/basis/:basisId/*');

    const basisId = basisMatch?.params?.basisId || basisNestedMatch?.params?.basisId || null;

    const [currency, setCurrencyState] = useState(initialCurrency || 'Toman');
    const [loading, setLoading] = useState(false);

    /**
     * تابع داخلی برای ست کردن currency
     * هم state را آپدیت می‌کند
     * هم مقدار global را برای currencyText آپدیت می‌کند
     */
    const updateCurrency = useCallback((nextCurrency) => {
        if (!nextCurrency) return;

        const nextCurrencyLabel = currencyToLabel(nextCurrency);

        globalCurrency = nextCurrency;
        globalCurrencyLabel = nextCurrencyLabel;

        setCurrencyState(nextCurrency);
    }, []);

    /**
     * Sync currency from backend
     */
    const syncCurrencyFromBackend = useCallback(async () => {
        if (!basisId) return;

        try {
            setLoading(true);

            const res = await fetch(`${ApiUrl}/api/basis/${basisId}`, {
                method: 'GET',
                headers: AuthHeader
            });

            const response = await res.json();
            const data = response?.data;

            if (data?.currency) {
                updateCurrency(data.currency);
            }
        } catch (error) {
            console.error('Failed to sync currency from backend:', error);
        } finally {
            setLoading(false);
        }
    }, [basisId, updateCurrency]);

    /**
     * فقط مقدار currency در context را تغییر می‌دهد.
     * ذخیره در دیتابیس بهتر است داخل فرم Basis انجام شود.
     */
    const setCurrency = useCallback(
        (nextCurrency) => {
            updateCurrency(nextCurrency);
        },
        [updateCurrency]
    );

    /**
     * label قابل استفاده داخل Context
     */
    const currencyLabel = currencyToLabel(currency);

    /**
     * در mount اولیه، مقدار global را با initialCurrency هماهنگ کن
     */
    useEffect(() => {
        updateCurrency(currency);
    }, []);

    /**
     * دریافت اولیه currency از دیتابیس
     */
    useEffect(() => {
        if (!basisId) return;

        syncCurrencyFromBackend();
    }, [basisId, syncCurrencyFromBackend]);

    /**
     * Sync when user focuses tab/window again
     */
    useEffect(() => {
        const handleFocus = () => {
            if (!basisId) return;

            syncCurrencyFromBackend();
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [basisId, syncCurrencyFromBackend]);

    const value = {
        currency,
        setCurrency,
        currencyLabel,
        loading,
        syncCurrencyFromBackend,
        basisId
    };

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    const context = useContext(CurrencyContext);

    if (!context) {
        throw new Error('useCurrency must be used inside CurrencyProvider');
    }

    return context;
}
